import { Directive, ElementRef, inject, input, output, signal } from "@angular/core";

import { EvalFileService } from "./eval-file.lib.service";
import { FileUploadBoxState, UploadErrorEvent } from "./file-upload-box";

@Directive({
	selector: '[mediaFileDragDrop]',
	standalone: true,
	host: {
		'[class.hover]': 'isHover()',
		'[class.dragover]': 'isDragOver()',
		'[class.selection-error]': 'hasError()',
		'(mouseover)': 'onMouseOver($event)',
		'(mouseleave)': 'onMouseLeave($event)',
		'(dragover)': 'onDragOver($event)',
		'(dragleave)': 'onDragLeave($event)',
		'(drop)': 'onDrop($event)',
	}
})
export class FileDragDropDirective {
	private elRef = inject(ElementRef);
	private evalService = inject(EvalFileService);


	readonly allowed_extensions = input<Array<string>>([]);
	readonly allowed_number = input<number>(1);
	readonly allowed_max_size = input<number>(1024 * 1024 * 1024);
	readonly mediaFiles = input<{
		orgFilename: string;
		filesize: number;
	}[]>(undefined);
	readonly filesInUpload = input<{filename: string}[]>(undefined);
	readonly state = input<FileUploadBoxState>(undefined);

	readonly filesChange = output<File[]>();
	readonly uploadErrorEvent = output<UploadErrorEvent>()

	readonly isHover = signal<boolean>(false);
	readonly isDragOver = signal<boolean>(false);
	readonly hasError = signal<boolean>(false);

	onMouseOver(_event: MouseEvent) {
		this.elRef.nativeElement.parentElement.classList.add('alt-bg');
		const state = this.state();
		if (state === FileUploadBoxState.DEFAULT) {
			this.elRef.nativeElement.parentElement.classList.add('show-desc');
			this.isHover.set(true);
			this.isDragOver.set(false);
		}
		if (state === FileUploadBoxState.UPLOADED){
			this.elRef.nativeElement.parentElement.classList.add('uploaded-hovered');
			this.isHover.set(true);
			this.isDragOver.set(false);
		}
	}
	onMouseLeave(_event: MouseEvent) {
		const state = this.state();
  		if (state === FileUploadBoxState.DEFAULT) {
			this.elRef.nativeElement.parentElement.classList.remove('alt-bg');
		}
		if (state === FileUploadBoxState.UPLOADED){
			this.elRef.nativeElement.parentElement.classList.remove('uploaded-hovered');
		}
		this.elRef.nativeElement.parentElement.classList.remove('show-desc');
		this.isHover.set(false);
	}

	onDragOver(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();

		// only execute on the first dragover event
		if (!this.isDragOver()) {
			this.hasError.set(false);
			this.isDragOver.set(true);
			this.elRef.nativeElement.parentElement.classList.add('alt-bg');
			this.elRef.nativeElement.parentElement.classList.add('show-desc');
		}
	}

	onDragLeave(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver.set(false);
		if (this.state() === FileUploadBoxState.DEFAULT) {
			this.elRef.nativeElement.parentElement.classList.remove('alt-bg');
		}
		this.elRef.nativeElement.parentElement.classList.remove('show-desc');
	}

	onDrop(event: DragEvent) {
		event.preventDefault();
		event.stopPropagation();
		this.isDragOver.set(false);
		this.elRef.nativeElement.parentElement.classList.remove("dragover");
		this.elRef.nativeElement.parentElement.classList.remove("show-desc");

		if (this.state() !== FileUploadBoxState.DEFAULT) {
			return;
		}

		this.hasError.set(this.evalService.evaluate(
			Object.values(event.dataTransfer?.files ?? []),
			{
				max: this.allowed_number(),
				max_size: this.allowed_max_size(),
				extensions: this.allowed_extensions(),
				is_duplicate: (file: File) => this.evalService.isDuplicate(file, this.mediaFiles(), this.filesInUpload())
			},
			this.filesChange,
			this.uploadErrorEvent
		));
	}
}
